import React, { useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
    Box,
    Grid,
    Checkbox,
    TextField,
    IconButton,
    FormControl,
    Autocomplete,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import FlagIcon from '@mui/icons-material/Flag';
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import FroalaEditorComponent from "../froalaHtmlEditor/FroalaEditorComponent";
import LocationInput from "./LocationInput";
import AICommentHelper from "../../components/CommentLibrary/CommentLibraryTags/AICommentHelper";
import { resolveLiveRating } from "./lineRatingUtils";

const froalaConfig = {
    imagePaste: false,
    spellCheck: true,
    heightMin: 201,
    heightMax: 300,
    linkAutoPrefix: 'https://',
    linkAlwaysBlank: true,
    toolbarButtons: {
        moreText: { buttons: ['bold', 'italic', 'underline', '|', 'fontFamily', 'fontSize', 'textColor', 'backgroundColor'], align: 'left', buttonsVisible: 7 },
        moreParagraph: { buttons: ['|', 'alignLeft', 'alignCenter', 'alignRight', 'formatOLSimple', 'formatUL'], align: 'left', buttonsVisible: 8 },
        moreRich: { buttons: ['|', "insertLink", "outdent", "indent"], align: "left", buttonsVisible: 1 },
        moreMisc: { buttons: ['undo', 'redo', 'selectAll', 'html', 'clearFormatting'], align: 'right', buttonsVisible: 2 }
    },
    toolbarButtonsXS: [['fontFamily', 'fontSize', 'bold', 'italic', 'underline', '|'], ['undo', 'redo']],
    pluginsEnabled: ["wordPaste", "spellChecker", "codeBeautifier", "fontFamily", "fontSize", "colors", "align", "lists", 'paragraphFormat', "link"],
};

const CLAddCommentNHT = ({ state, setState, isEdit, tagOptions, locationOptions, ratingOptions }) => {
    const {
        commentTitle,
        commentTag,
        commentText,
        commentDropdownList,
        openAddDialog,
    } = state;
    const [showAIModal, setShowAIModal] = useState(false);
    const storeData = useSelector((state) => state.commentLibrary);
    const commentEditor = useRef();

    const handleEditorChange = (name, content) => {
        setState((prev) => ({
            ...prev,
            [name]: { ...prev[name], innerHTML: content },
        }));
    };

    const handleDropdownList = (name, value) => {
        setState((prev) => ({
            ...prev,
            commentDropdownList: value,
        }));
    };

    const handleChangeTag = (name, value) => {
        const modifiedArray = value.map((obj) => ({
            id: obj.id,
            name: obj.title || obj.name,
            color: obj.color,
        }));
        setState((prev) => ({ ...prev, [name]: modifiedArray }));
    };

    const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
    const checkedIcon = <CheckBoxIcon fontSize="small" />;

    const resolveColor = (item, ratingOptions = []) => {
        if (!item) return "#CED2DA";
        const live = resolveLiveRating(item, ratingOptions);
        const c = live.color ?? live.Color ?? live.colour;
        if (typeof c === "string") return c;
        if (c && typeof c === "object") {
            return c.hex ?? c.code ?? c.value ?? c.name ?? "#CED2DA";
        }
        return "#CED2DA";
    };

    const isTruthy = (v) => {
        if (v == null) return false;
        if (typeof v === "boolean") return v;
        if (typeof v === "number") return v !== 0;
        if (typeof v === "string") return ["1", "true", "yes", "y"].includes(v.toLowerCase());
        return false;
    };

    const resolveIncludeInSummary = (item, ratingOptions = []) => {
        if (!item) return false;

        const live = resolveLiveRating(item, ratingOptions);

        // common keys and fallbacks
        let v =
            live.include_in_summary ??
            live.includeInSummary ??
            live.is_flagged ??
            live.isFlagged ??
            live.flag ??
            live.include ??
            live?.summary?.include_in_summary;

        // object-like shapes e.g. { value: 1 } or { enabled: true }
        if (v && typeof v === "object") {
            v = v.value ?? v.enabled ?? v.bool ?? v.checked ?? v.flag;
        }

        return isTruthy(v);
    };

    const resolveName = (item, ratingOptions = []) => {
        if (!item) return "";
        const live = resolveLiveRating(item, ratingOptions);
        return live?.Name ?? live?.name ?? "";
    };

    return (
        <>
            <AICommentHelper showAIModal={showAIModal} setShowAIModal={setShowAIModal} />
            <Box p={0.5} fullWidth sx={{ width: "100%" }}>
                <Grid container spacing={2}>
                    {/* TITLE */}
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <label className="cl-new-comment-input-form-label">TITLE</label>
                            <TextField
                                size="small"
                                variant="outlined"
                                value={commentTitle}
                                onChange={(e) =>
                                    setState((prev) => ({ ...prev, commentTitle: e.target.value }))
                                }
                            />
                        </FormControl>
                    </Grid>
                    {/* TAGS */}
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <label className="cl-new-comment-input-form-label">TAGS</label>
                            <Autocomplete
                                multiple
                                size="small"
                                limitTags={2}
                                options={tagOptions || storeData?.clTagList || []}
                                value={commentTag}
                                onChange={(e, value) => {
                                    handleChangeTag("commentTag", value);
                                }}
                                disableCloseOnSelect
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                getOptionLabel={(option) => option?.name || ""}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => {
                                        const tagProps = getTagProps({ index });
                                        return (
                                            <Box
                                                key={option?.id}
                                                {...tagProps}
                                                sx={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    gap: 0.75,
                                                    px: 0.5,
                                                    py: 0,
                                                    pl: 1,
                                                    borderRadius: "16px",
                                                    backgroundColor: "#66A3E3",
                                                    border: "1px solid #E5E7EB",
                                                    color: "#1F2937",
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {/* removed color dot for chip */}
                                                <span style={{ maxWidth: 140, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                    {option?.name}
                                                </span>
                                                <IconButton size="small" onClick={tagProps.onDelete} sx={{ height: 20, width: 20, ml: 0.25, color: "#fff", backgroundColor: "#0076CC" }}>
                                                    <CloseOutlinedIcon sx={{ fontSize: 14 }} />
                                                </IconButton>
                                            </Box>
                                        );
                                    })
                                }
                                renderOption={(props, option, { selected }) => (
                                    <li {...props} key={option?.id}>
                                        <Checkbox
                                            icon={icon}
                                            checkedIcon={checkedIcon}
                                            checked={commentTag?.some((tag) => tag.id === option.id)}
                                        />
                                        <Box
                                            sx={{
                                                width: 12,
                                                height: 12,
                                                backgroundColor: option?.color || option?.Color,
                                                borderRadius: "50%",
                                                marginRight: 1,
                                            }}
                                        />
                                        {option?.name}
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField {...params} variant="outlined" placeholder={(state?.commentTag || []).length === 0 ? "Add tags" : ""} />
                                )}
                            />
                        </FormControl>
                    </Grid>

                    {/* RATING */}
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <label className="cl-new-comment-input-form-label">RATING</label>
                            <Autocomplete
                                multiple
                                size="small"
                                limitTags={2}

                                options={ratingOptions || []}
                                value={state?.commentRating || []}
                                onChange={(e, value) => {
                                    handleChangeTag("commentRating", value);
                                }}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                disableCloseOnSelect
                                getOptionLabel={(option) => option?.name || ""}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => {
                                        const tagProps = getTagProps({ index });
                                        const color = resolveColor(option, ratingOptions);
                                        const flagged = resolveIncludeInSummary(option, ratingOptions);
                                        const name = resolveName(option, ratingOptions);
                                        return (
                                            <Box
                                                key={option?.id}
                                                {...tagProps}
                                                sx={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    gap: 0.75,
                                                    px: 0.5,
                                                    py: 0,
                                                    pl: 1,
                                                    borderRadius: "16px",
                                                    backgroundColor: "#FFF",
                                                    border: "1px solid #CED2DA",
                                                    color: "#637083",
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        height: 15,
                                                        width: 15,
                                                        backgroundColor: color,
                                                        borderRadius: 5,
                                                    }}
                                                >
                                                    {flagged ? <FlagIcon sx={{ color: "white", fontSize: 15 }} /> : null}
                                                </span>
                                                <span
                                                    style={{
                                                        maxWidth: 140,
                                                        color: "#637083",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                        marginTop: 2,
                                                    }}
                                                >
                                                    {name}
                                                </span>
                                                <IconButton size="small" onClick={tagProps.onDelete} sx={{ height: 20, width: 20, ml: 0.25, color: "#fff", backgroundColor: "#97A1AF" }}>
                                                    <CloseOutlinedIcon sx={{ fontSize: 14 }} />
                                                </IconButton>
                                            </Box>
                                        );
                                    })
                                }
                                renderOption={(props, option, { selected }) => (
                                    <li {...props} key={option?.id}>
                                        <Checkbox
                                            icon={icon}
                                            checkedIcon={checkedIcon}
                                            checked={(state?.commentRating || [])?.some((tag) => tag.id === option.id)}
                                        />
                                        <Box
                                            sx={{
                                                width: 15,
                                                height: 15,
                                                backgroundColor: option?.color,
                                                borderRadius: "2px",
                                                marginRight: 1,
                                            }}
                                        >

                                            {option?.include_in_summary === 1 ? <FlagIcon sx={{ color: "white", fontSize: 15, mb: 0.3 }} /> : ""}
                                        </Box>
                                        {option?.name}
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField {...params} variant="outlined" placeholder={(state?.commentRating || []).length === 0 ? "No ratings selected" : ""} />
                                )}
                            />
                        </FormControl>
                    </Grid>

                    {/* LOCATION */}
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <label className="cl-new-comment-input-form-label">LOCATION</label>
                            <LocationInput
                                cmtLocHolder
                                nonHT={true}
                                action="comment"
                                locationData={locationOptions || []}
                                onApplySelection={(selected) => setState((prev) => ({ ...prev, location: selected }))}
                                locationValue={Array.isArray(state?.location) ? state.location : (state?.location ? [state.location] : [])}
                            />
                        </FormControl>
                    </Grid>

                    {/* COMMENT */}
                    <Grid item xs={12} sm={12}>
                        <FormControl fullWidth>
                            <label id="commentText" className="cl-new-comment-input-form-label" htmlFor="comment">COMMENT</label>
                            <FroalaEditorComponent
                                commentData={{ commentText: commentText, comment_dropdowns: commentDropdownList }}
                                editor={commentEditor}
                                onChange={handleEditorChange}
                                id={"commentText"}
                                editorData={commentText}
                                showAI={false}
                                showAIModal={showAIModal}
                                setShowAIModal={setShowAIModal}
                                setEditorContent={setState}
                                froalaConfig={froalaConfig}
                                isEdit={isEdit}
                                handleDropdownList={handleDropdownList}
                                type={isEdit ? "edit" : "add"}
                                key={isEdit ? `edit` : 'add'}
                                filterTagList={tagOptions || storeData?.clTagList}
                                newOpen={openAddDialog}
                            />
                        </FormControl>
                    </Grid>
                </Grid>
            </Box>
        </>
    );
};

export default CLAddCommentNHT;
